// ============================================================
// LinuxSystemd.jsx
//
// A branded, responsive course content page for the
// "systemd & Service Management" lesson.
//
// Author: ProDev Linux Series
// Features: Service management commands, custom unit file
//           structure, and responsive Material UI layout.
// ============================================================

import React, { useState, useEffect } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SettingsSuggestIcon    from "@mui/icons-material/SettingsSuggest";
import RocketLaunchIcon      from "@mui/icons-material/RocketLaunch";
import ListAltIcon           from "@mui/icons-material/ListAlt";
import NoteAddIcon           from "@mui/icons-material/NoteAdd";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon              from "@mui/icons-material/Link";
import ExpandMoreIcon        from "@mui/icons-material/ExpandMore";
import ExpandLessIcon        from "@mui/icons-material/ExpandLess";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon              from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5, height: "100%", background: BRAND.surface,
            border: `1px solid ${BRAND.border}`, borderRadius: 3,
            transition: "0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5 }}>
            <Box onClick={() => setOpen(!open)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{
            display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none",
            border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "0.25s",
            "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` },
        }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box><Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{sub}</Typography>}</Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function LinuxSystemd() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Administration · Module 06"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Linux <span>systemd</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        systemd is the standard init system for modern Linux. It is the architect that manages your system startup, background services, and resource allocation.
                    </Typography>
                </Box>

                {/* WHAT IS SYSTEMD? */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsSuggestIcon />} label="The Init System" />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 } }}>
                        As the first process to start (PID 1), <strong style={{ color: BRAND.white }}>systemd</strong> acts as the parent to all other processes.
                        It uses "unit files" to define how services, mounts, and devices should behave. Whether you are running a web server or a database,
                        systemd ensures your application stays running and starts automatically after a reboot.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* KEY CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ListAltIcon />} label="Key Management Concepts" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <TerminalIcon />, title: "systemctl", description: "The primary command-line tool used to introspect and control the state of the systemd system and service manager." },
                            { icon: <NoteAddIcon />, title: "Unit Files", description: "Configuration files (.service, .target, .timer) found in /etc/systemd/system/ that define system resources." },
                            { icon: <RocketLaunchIcon />, title: "Daemons", description: "Background processes that run independently of user intervention, managed as 'services' by systemd." },
                        ].map((card) => (
                            <Grid item xs={12} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                {/* SERVICE MANAGEMENT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Service Management" />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🚀 Controlling Services</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ systemctl status nginx",  comment: "Check if a service is running or has errors" },
                        { cmd: "$ sudo systemctl start nginx",  comment: "Activate a service immediately" },
                        { cmd: "$ sudo systemctl stop nginx",   comment: "Shut down a service" },
                        { cmd: "$ sudo systemctl restart nginx",comment: "Stop and then start a service again" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>⚙️ Boot Configuration</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo systemctl enable nginx", comment: "Set service to start automatically on boot" },
                        { cmd: "$ sudo systemctl disable nginx",comment: "Prevent service from starting on boot" },
                        { cmd: "$ systemctl is-enabled ssh",    comment: "Verify current boot status" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>🔽 Custom Service Creation</Typography>
                    <ExpandableExample title="Anatomy of a .service file" icon={<NoteAddIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>
                            To create a persistent background app, define a file at <code style={{ color: BRAND.goldBright }}>/etc/systemd/system/myapp.service</code>:
                        </Typography>
                        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, p: 2.5, mb: 2 }}>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.gold, mb: 1 }}>[Unit]</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>Description=ProDev Python App</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2, mb: 1 }}>After=network.target</Typography>

                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.gold, mb: 1 }}>[Service]</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>ExecStart=/usr/bin/python3 /home/hazzan/app.py</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>Restart=always</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2, mb: 1 }}>User=hazzan</Typography>

                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.gold, mb: 1 }}>[Install]</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.white, ml: 2 }}>WantedBy=multi-user.target</Typography>
                        </Box>
                        <CodeBlock lines={[
                            { cmd: "$ sudo systemctl daemon-reload", comment: "Step 1: Tell systemd to find the new file" },
                            { cmd: "$ sudo systemctl enable myapp",  comment: "Step 2: Enable at boot" },
                            { cmd: "$ sudo systemctl start myapp",   comment: "Step 3: Launch now" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="Listing & Inspecting" icon={<ListAltIcon />}>
                        <CodeBlock lines={[
                            { cmd: "$ systemctl list-units --type=service", comment: "List all currently loaded services" },
                            { cmd: "$ systemctl list-unit-files",           comment: "List every available unit file and its state" },
                            { cmd: "$ journalctl -u nginx",                 comment: "View specific logs for the nginx service" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Module Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Understand systemd's role as PID 1",
                            "Monitor service health with systemctl status",
                            "Control service lifecycles (Start, Stop, Restart)",
                            "Configure persistent services that survive reboots",
                            "Draft custom unit files for proprietary applications",
                            "Introspect service logs via journalctl",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* RESOURCES */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Technical Documentation" />
                    <ResourceLink href="https://www.freedesktop.org/wiki/Software/systemd/" label="systemd Homepage" sub="Official technical portal for the systemd software suite." />
                    <ResourceLink href="https://wiki.archlinux.org/title/Systemd" label="Arch Wiki: systemd" sub="The industry-gold standard for systemd documentation." />
                    <ResourceLink href="https://linuxize.com/post/how-to-use-systemctl/" label="How to use systemctl" sub="Practical, command-heavy guide for daily administration." />
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Junior Dev Tip:</strong> If your service fails to start, the first thing you should run is <code style={{ color: BRAND.goldBright }}>journalctl -xe</code>. This provides the "exhausted" log output which usually explains exactly what went wrong in your configuration.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
